/**
 * Platform-authenticator setup deep links.
 *
 * Resolved at runtime from a parsed UA hint. Returns the most
 * direct "open the setting that lets the user enable a platform
 * authenticator" target the OS exposes — and falls back to a
 * documentation URL when no first-class scheme is available.
 *
 * Why deep links matter (incident-driven, 2026-05-08):
 *
 * The post-error copy on `platform_unavailable` told the user to
 * "set up Windows Hello in your system settings, then come back."
 * That's correct in content but useless in execution: the user
 * doesn't know where Settings is, doesn't know what tab inside
 * Settings, and doesn't know which sub-page. A clickable button
 * that opens the EXACT panel turns a 5-minute support ticket
 * into a 30-second self-service step. This is the canonical
 * gold-standard pattern Microsoft / Apple / Google ship with
 * their own consumer auth flows.
 *
 * Each link's stability has been verified against the platform's
 * documented URI scheme registry, NOT inferred. Where a scheme is
 * non-public (no formal registry entry, e.g. macOS) we fall back
 * to a documentation URL — better to send the user to a
 * step-by-step guide than to a broken `x-apple.systempreferences:`
 * URL the next OS release silently retired.
 *
 * Out of scope:
 *
 *   - Auto-opening the link without an explicit user click. Most
 *     OSes prompt before honouring custom-scheme navigation; an
 *     unprompted window.location assign would either no-op or
 *     show a permission dialog with no context. We render a
 *     button and let the user click.
 *
 *   - Detecting whether the user actually completed setup.
 *     There's no callback. The "I've set it up — try again"
 *     button calls `invalidateProbeCache()` and re-probes; if the
 *     user lied, the probe still says no and the screen stays
 *     where it is.
 */

import type { UaHint } from "./device-capabilities";

/** What the consumer renders. `url` is the click target;
 *  `label` is the localised button text; `kind` lets the
 *  consumer style the button differently for native deep links
 *  (typically a primary action) vs documentation fallbacks
 *  (typically a secondary "open instructions" link). */
export interface PlatformAuthenticatorSetupLink {
  url: string;
  label: string;
  /** "deep_link" — direct OS-scheme URI that opens a Settings
   *  panel. "doc" — fallback documentation URL when no stable
   *  deep link exists. */
  kind: "deep_link" | "doc";
}

/**
 * Resolve the best setup link for the user's current device.
 *
 * Returns null only when the OS is genuinely unknown (UA hint
 * `os === "Unknown"`); every other case maps to either a
 * deep-link or a documented fallback URL.
 */
export function platformAuthenticatorSetupLink(
  ua: UaHint,
): PlatformAuthenticatorSetupLink | null {
  switch (ua.os) {
    case "Windows":
      // ms-settings: scheme is in Microsoft's published URI registry
      // and stable across Windows 10 / 11 (the registry entry has
      // been honoured since Windows 10 build 1703, May 2017).
      // `signinoptions` page is exactly where Windows Hello PIN /
      // fingerprint / face are configured.
      // https://learn.microsoft.com/en-us/windows/uwp/launch-resume/launch-settings-app
      return {
        url: "ms-settings:signinoptions",
        label: "Set up Windows Hello",
        kind: "deep_link",
      };

    case "macOS":
      // x-apple.systempreferences:com.apple.Touch-ID-Settings.extension
      // is the verified-stable deep link for the Touch ID & Password
      // panel on macOS Ventura (13) through Sequoia (15). On older
      // macOS (Big Sur and earlier) the panel was named differently
      // ("Touch ID") and the same scheme didn't work; those users
      // see "the link didn't open" because the OS silently no-ops
      // an unknown URI host. Our fallback-hint UI (rendered after
      // a 1.2s delay by `openSetupLink`) surfaces the URL for
      // manual copy in that case, so the user can navigate
      // System Settings → Touch ID & Password by hand.
      //
      // The 1Password / Bitwarden teams use a similar pattern; the
      // tradeoff is "deep-link works on most users, fallback for
      // older macOS" vs "send everyone to a doc page." The former
      // is materially better UX for the common case.
      return {
        url: "x-apple.systempreferences:com.apple.Touch-ID-Settings.extension",
        label: "Set up Touch ID",
        kind: "deep_link",
      };

    case "Android":
      // Android has no first-class scheme to deep-link into a
      // specific Settings panel from the browser (the
      // `intent://` scheme works only from Android-native apps,
      // not from a web context). We point at Google's published
      // help page; the user picks their Android version's flow
      // from the page's selector.
      return {
        url: "https://support.google.com/android/answer/9079129",
        label: "Set up screen lock",
        kind: "doc",
      };

    case "iOS":
      // iOS has `app-settings:` but it only opens YOUR app's
      // Settings page, not the system Settings panel for Face ID
      // / Touch ID. Same fallback pattern as macOS.
      return {
        url: "https://support.apple.com/HT204060",
        label: "Set up Face ID or Touch ID",
        kind: "doc",
      };

    case "Linux":
      // No standard "set up biometric" panel across Linux DEs;
      // GNOME / KDE / Hyprland / etc. each have their own UX.
      // Generic guidance is the honest answer.
      return {
        url: "https://www.passkeys.io/who-supports-passkeys",
        label: "How to enable passkeys on this device",
        kind: "doc",
      };

    case "Unknown":
    default:
      return null;
  }
}

/**
 * Helper: try to open the deep link via a temporary anchor. The
 * mechanism uses an anchor (rather than `window.location.assign`)
 * so the browser's native security UI for custom-scheme
 * navigation fires reliably across Chrome / Edge / Firefox.
 *
 * Documentation links are opened in a NEW TAB so the user
 * doesn't lose the auth flow. Deep links open in the SAME tab
 * because the OS hijacks navigation immediately and there's no
 * tab-switching concern.
 *
 * Failure modes the consumer should surface a fallback for:
 *   - Custom-scheme navigation refused by the browser
 *     (Firefox in some configs prompts; in stricter configs it
 *     silently no-ops).
 *   - Pop-up blocker swallows the doc-fallback `_blank` open.
 *   - macOS Big Sur and earlier silently no-op the
 *     `x-apple.systempreferences:` scheme used for Touch ID.
 *
 * The synchronous return value can't tell the consumer which
 * (or any) of those happened — the browser doesn't expose a
 * navigation-success event for these calls. So consumers
 * should ALSO render a 1.2-second-delayed "If Settings didn't
 * open" hint via `<SetupLinkFallback>` (or equivalent) and
 * keep the URL accessible for manual copy.
 *
 * Returns true on attempt (browser context present, click
 * dispatched), false when the document is missing
 * (SSR / non-browser test env). Never throws — link clicks
 * are a UX nicety, not a correctness boundary.
 */
export function openSetupLink(link: PlatformAuthenticatorSetupLink): boolean {
  if (typeof document === "undefined") return false;
  const a = document.createElement("a");
  a.href = link.url;
  if (link.kind === "doc") {
    a.target = "_blank";
    a.rel = "noopener noreferrer";
  }
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  return true;
}

/**
 * Recommended delay after which a consumer should reveal a
 * "If Settings didn't open, try this link" fallback hint.
 *
 * 1.2s is a deliberate choice:
 *   - Long enough that a successful OS hand-off has had time
 *     to take focus away from the browser tab (typical
 *     handoff is <300ms; 1.2s leaves slack for slow OSes).
 *   - Short enough that a user staring at an unchanged
 *     screen doesn't conclude the button is broken.
 *
 * Exported so the UI layer can use the same constant in
 * setTimeout calls instead of guessing — keeps the
 * fallback-reveal timing consistent across consumers.
 */
export const SETUP_LINK_FALLBACK_DELAY_MS = 1200;
